import { Typography, CircularProgress, Button, Stack, Icon, Box } from '@ace-clublabs/shared-component-library';
import { numberHelper } from '../utils/numberHelper';
import { benefitsHelper } from '../utils/benefitsHelper';
import { useContext, useEffect, useState } from 'react';
import { MembershipAppStateContext, MembershipAppStateInitializer } from './MembershipAppState/MembershipAppState';
import { CartProductCode, CurrentPayPlan, FlowType, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { useHistory } from 'react-router-dom';
import { convertBillPlanToTerm, isAutopayRequired } from '../utils/helpers';
import { DiscountRule } from '@lib/types';
import { stringHelper } from '../utils/stringHelper';

interface ITotalCostHeader {
  formattedCost?: string;
  strikethrough?: boolean;
  recurringPeriodLabel?: string;
  onChangeClick: () => void;
  showBenefitBullets?: boolean;
}

export const TotalCostHeader = ({
  formattedCost,
  strikethrough,
  recurringPeriodLabel,
  onChangeClick,
  showBenefitBullets,
}: ITotalCostHeader) => {
  const history = useHistory();
  const { membershipInfo, sessionData, isPriceCartLoading } = useContext(MembershipAppStateContext);
  const membershipLevel = sessionData.formData.membershipLevel;
  const [membershipTitle, setMembershipTitle] = useState('');
  const hideChangeButton =
    sessionData.flowType === FlowType.MAJOR_ROCKET || membershipInfo?.allowedMembershipLevels?.length! <= 1;
  const ml =
    membershipLevel == undefined ||
    membershipLevel == MembershipLevel.CLASSIC_RV ||
    membershipLevel == MembershipLevel.CLASSIC
      ? MembershipLevel.CLASSIC
      : membershipLevel == MembershipLevel.PLUS_RV || membershipLevel == MembershipLevel.PLUS
      ? MembershipLevel.PLUS
      : membershipLevel == MembershipLevel.PREMIER_RV || membershipLevel == MembershipLevel.PREMIER
      ? MembershipLevel.PREMIER
      : membershipLevel;

  //check if there's free associate
  const term = convertBillPlanToTerm(sessionData.formData?.billPlan!);
  const hasFreeAssociate = membershipInfo?.membershipCost?.[ml]?.[term]?.membershipDiscounts?.find(
    (rule) => rule?.rule == DiscountRule.FREE_ASSOCIATE
  );

  //Ger primary member discount for T-Mobile display
  const primaryDiscountAmount = membershipInfo?.membershipCost?.[ml]?.[term]?.primary?.discountAmount || 0;

  //check if the admission fee is waived
  const admissionFeeCost = sessionData?.priceCart?.items.find((item) => item.code === CartProductCode.ADMISSION);
  const admissionFeeWaived = admissionFeeCost?.discountedPrice === 0;

  //check if employee discount is applied
  const employeeDiscountApplied = sessionData.flowType === FlowType.EMPLOYEE && primaryDiscountAmount;

  //check if the autopay discount is applied
  const autopayDiscount = sessionData?.priceCart?.items.find((item) => item.code === CartProductCode.AUTOPAY_DISCOUNT);
  const autopayDiscountApplied = (autopayDiscount?.price || 0) > 0;

  //check if it's recurring monthly
  const isRecurringMonthly = sessionData.formData?.billPlan === CurrentPayPlan.RECURRINGMONTHLY;

  //get the gift card value
  const giftCardValue = membershipInfo?.giftDetails?.giftCardValue;

  const BenefitBulletsSection = () => {
    return (
      <div className="card-benefits-section">
        <Stack sx={{ gap: '4px', marginBottom: '14px' }}>
          {hasFreeAssociate && hasFreeAssociate.displayCount > 0 ? (
            <Stack direction="row" sx={{ marginLeft: '16px', gap: '4px' }} alignItems="center">
              <Icon color="primary" sx={{ fontSize: '1.25rem' }} iconName="check-regular" />
              <Typography
                className="free-member-bullet"
                variant="b1Medium"
                sx={{ color: '#404040', lineHeight: '28px' }}
              >
                {`${hasFreeAssociate.displayCount} free or reduced cost family member${hasFreeAssociate.displayCount > 1 ? 's' : ''}`}
              </Typography>
            </Stack>
          ) : (
            <></>
          )}
          {sessionData.flowType === FlowType.T_MOBILE && primaryDiscountAmount ? (
            <Stack direction="row" sx={{ marginLeft: '16px', gap: '4px' }} alignItems="center">
              <Icon color="primary" sx={{ fontSize: '1.25rem' }} iconName="check-regular" />
              <Typography
                className="t-mobile-year-on-us-bullet"
                variant="b1Medium"
                sx={{ color: '#404040', lineHeight: '28px' }}
              >
                Year on Us by T-Mobile ({stringHelper.formatMoney(primaryDiscountAmount.toString())} value)
              </Typography>
            </Stack>
          ) : (
            <></>
          )}
          {employeeDiscountApplied ? (
            <Stack direction="row" sx={{ marginLeft: '16px', gap: '4px' }} alignItems="center">
              <Icon color="primary" sx={{ fontSize: '1.25rem' }} iconName="check-regular" />
              <Typography
                className="t-mobile-year-on-us-bullet"
                variant="b1Medium"
                sx={{ color: '#404040', lineHeight: '28px' }}
              >
                Employee discount ({stringHelper.formatMoney(primaryDiscountAmount.toString())} value)
              </Typography>
            </Stack>
          ) : (
            <></>
          )}
          {admissionFeeWaived ? (
            <Stack direction="row" sx={{ marginLeft: '16px', gap: '4px' }} alignItems="center">
              <Icon color="primary" sx={{ fontSize: '1.25rem' }} iconName="check-regular" />
              <Typography
                className="admission-waived-bullet"
                variant="b1Medium"
                sx={{ color: '#404040', lineHeight: '28px' }}
              >
                Waived admission fee
              </Typography>
            </Stack>
          ) : (
            <></>
          )}
          {autopayDiscountApplied ? (
            <Stack direction="row" sx={{ marginLeft: '16px', gap: '4px' }} alignItems="center">
              <Icon color="primary" sx={{ fontSize: '1.25rem' }} iconName="check-regular" />
              <Typography
                className="autopay-discount-bullet"
                variant="b1Medium"
                sx={{ color: '#404040', lineHeight: '28px' }}
              >
                Auto Pay discount applied
              </Typography>
            </Stack>
          ) : (
            <></>
          )}
          {sessionData.flowType === FlowType.GIFT && giftCardValue && giftCardValue > 0 ? (
            <Stack direction="row" sx={{ marginLeft: '16px', gap: '4px' }} alignItems="center">
              <Icon color="primary" sx={{ fontSize: '1.25rem' }} iconName="check-regular" />
              <Typography
                className="t-mobile-year-on-us-bullet"
                variant="b1Medium"
                sx={{ color: '#404040', lineHeight: '28px' }}
              >
                ${giftCardValue} gift card
              </Typography>
            </Stack>
          ) : (
            <></>
          )}
        </Stack>
      </div>
    );
  };

  useEffect(() => {
    switch (membershipLevel) {
      case MembershipLevel.CLASSIC:
        setMembershipTitle('Classic');
        break;
      case MembershipLevel.PLUS:
        setMembershipTitle('Plus');
        break;
      case MembershipLevel.PREMIER:
        setMembershipTitle('Premier');
        break;
    }
  }, [membershipLevel]);

  return (
    <>
      <div className="membership-card-container">
        <div className="card-details">
          <Typography className="card-title" variant="b1Bold" sx={{ fontWeight: '700' }}>
            Total cost of {membershipTitle}
          </Typography>
          <Stack direction="row" className="card-img-price" justifyContent="space-between">
            <Stack direction="row" gap="0.5rem">
              <Stack direction="row" alignItems="center" className="membership-card-total-container">
                {isPriceCartLoading ? (
                  <div style={{ marginLeft: 2 }}>
                    <CircularProgress variant="indeterminate" size={25} className="total-cost-spinner" />
                  </div>
                ) : (
                  <>
                    <Typography
                      className={strikethrough ? 'strikethrough membership-price-total' : 'membership-price-total'}
                      variant="b1Bold"
                      style={{ fontWeight: 700 }}
                    >
                      {formattedCost ?? numberHelper.formatMoney(sessionData?.priceCart?.total ?? 0.0)}
                    </Typography>
                    <div className="membership-price-label" style={{ whiteSpace: 'pre' }}>
                      {' '}
                      {recurringPeriodLabel}
                    </div>
                  </>
                )}
              </Stack>
            </Stack>

            <Box hidden={hideChangeButton} className="change-button-container">
              <Button
                variant="text"
                label={
                  <Typography variant="b1Regular" color={'#0C72DF'}>
                    Change
                  </Typography>
                }
                className="change-benefit-button"
                onClick={onChangeClick}
              />
            </Box>
          </Stack>
        </div>
      </div>
      {showBenefitBullets ? <> {isRecurringMonthly ? <></> : <BenefitBulletsSection />}</> : <></>}
    </>
  );
};
